import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Plus, 
  Search, 
  Filter, 
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  TrendingDown,
  Building,
  CreditCard,
  CalendarDays,
  Receipt
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const AccountsPayablePage = () => {
  const { company, person } = useAuth();
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewBillModal, setShowNewBillModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);

  const [newBill, setNewBill] = useState({
    supplier_id: '',
    supplier_name: '',
    description: '',
    amount: '',
    due_date: '',
    category: '',
    invoice_number: '',
    notes: '',
    priority: 'medium'
  });

  useEffect(() => {
    if (company?.id) {
      loadBills();
    }
  }, [company]);

  const loadBills = async () => {
    try {
      setLoading(true);
      
      // Carregar contas a pagar
      const billsData = await dataHelpers.getAccountsPayable(company.id);
      setBills(billsData || []);
      
      // Carregar fornecedores (pessoas do tipo supplier)
      const peopleData = await dataHelpers.getPeople(company.id);
      const suppliersData = peopleData?.filter(person => person.type === 'supplier') || [];
      setSuppliers(suppliersData);
      
    } catch (error) {
      console.error('Error loading bills:', error);
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'pending':
        return 'Pendente';
      case 'overdue':
        return 'Vencido';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'medium':
        return 'border-l-yellow-500';
      case 'low':
        return 'border-l-green-500';
      default:
        return 'border-l-gray-500';
    }
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = bill.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleCreateBill = async () => {
    try {
      const selectedSupplier = suppliers.find(s => s.id === parseInt(newBill.supplier_id));
      
      const billData = {
        supplier_id: newBill.supplier_id ? parseInt(newBill.supplier_id) : null,
        supplier_name: selectedSupplier ? selectedSupplier.name : newBill.supplier_name,
        description: newBill.description,
        amount: parseFloat(newBill.amount),
        due_date: newBill.due_date,
        category_name: newBill.category,
        invoice_number: newBill.invoice_number,
        notes: newBill.notes,
        priority: newBill.priority,
        created_by: person.id,
        status: 'pending'
      };
      
      await dataHelpers.createAccountsPayable(company.id, billData);
      toast.success('Conta a pagar criada com sucesso!');
      
      setShowNewBillModal(false);
      setNewBill({
        supplier_id: '',
        supplier_name: '',
        description: '',
        amount: '',
        due_date: '',
        category: '',
        invoice_number: '',
        notes: '',
        priority: 'medium'
      });
      
      loadBills();
    } catch (error) {
      console.error('Error creating bill:', error);
      toast.error('Erro ao criar conta a pagar');
    }
  };

  const handleEditBill = (bill) => {
    setEditingBill({
      id: bill.id,
      supplier_name: bill.supplier_name,
      description: bill.description,
      amount: bill.amount.toString(),
      due_date: bill.due_date,
      category: bill.category_name,
      invoice_number: bill.invoice_number,
      notes: bill.notes,
      priority: bill.priority,
      status: bill.status
    });
    setShowEditModal(true);
  };

  const handleUpdateBill = async () => {
    try {
      const billData = {
        supplier_name: editingBill.supplier_name,
        description: editingBill.description,
        amount: parseFloat(editingBill.amount),
        due_date: editingBill.due_date,
        category_name: editingBill.category,
        invoice_number: editingBill.invoice_number,
        notes: editingBill.notes,
        priority: editingBill.priority,
        status: editingBill.status
      };
      
      await dataHelpers.updateAccountsPayable(editingBill.id, billData);
      toast.success('Conta a pagar atualizada com sucesso!');
      
      setShowEditModal(false);
      setEditingBill(null);
      
      loadBills();
    } catch (error) {
      console.error('Error updating bill:', error);
      toast.error('Erro ao atualizar conta a pagar');
    }
  };

  const handleMarkAsPaid = async (bill) => {
    try {
      await dataHelpers.updateAccountsPayable(bill.id, { status: 'paid' });
      toast.success('Conta marcada como paga!');
      loadBills();
    } catch (error) {
      console.error('Error marking bill as paid:', error);
      toast.error('Erro ao marcar conta como paga');
    }
  };

  const calculateTotals = () => {
    const total = filteredBills.reduce((sum, bill) => sum + bill.amount, 0);
    const paid = filteredBills.filter(b => b.status === 'paid').reduce((sum, bill) => sum + bill.amount, 0);
    const pending = filteredBills.filter(b => b.status === 'pending').reduce((sum, bill) => sum + bill.amount, 0);
    const overdue = filteredBills.filter(b => b.status === 'overdue').reduce((sum, bill) => sum + bill.amount, 0);
    
    return { total, paid, pending, overdue };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contas a Pagar</h1>
            <p className="text-gray-600">
              Gerencie seus fornecedores e contas a pagar
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={() => setShowNewBillModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Conta
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(totals.total)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pago</p>
                <p className="text-2xl font-semibold text-green-600">
                  {formatCurrency(totals.paid)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pendente</p>
                <p className="text-2xl font-semibold text-yellow-600">
                  {formatCurrency(totals.pending)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Vencido</p>
                <p className="text-2xl font-semibold text-red-600">
                  {formatCurrency(totals.overdue)}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar fornecedor, descrição ou nota fiscal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="pending">Pendente</option>
              <option value="paid">Pago</option>
              <option value="overdue">Vencido</option>
            </select>

            <Button variant="outline" className="justify-center">
              <Filter className="w-4 h-4 mr-2" />
              Mais Filtros
            </Button>
          </div>
        </Card>

        {/* Bills List */}
        <Card>
          <div className="overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Fornecedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Vencimento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBills.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <TrendingDown className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Nenhuma conta a pagar encontrada</p>
                    </td>
                  </tr>
                ) : (
                  filteredBills.map((bill) => (
                    <tr key={bill.id} className={`hover:bg-gray-50 border-l-4 ${getPriorityColor(bill.priority)}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {bill.supplier_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            NF: {bill.invoice_number}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{bill.description}</p>
                        <p className="text-xs text-gray-500">{bill.category_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(bill.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {formatDate(bill.due_date)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                          {getStatusIcon(bill.status)}
                          <span className="ml-1">{getStatusLabel(bill.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          onClick={() => {
                            setSelectedBill(bill);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleEditBill(bill)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* New Bill Modal */}
      {showNewBillModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Conta a Pagar</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fornecedor
                </label>
                <select
                  value={newBill.supplier_id}
                  onChange={(e) => {
                    const supplierId = e.target.value;
                    const supplier = suppliers.find(s => s.id === parseInt(supplierId));
                    setNewBill(prev => ({ 
                      ...prev, 
                      supplier_id: supplierId,
                      supplier_name: supplier ? supplier.name : ''
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Selecione um fornecedor</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.document && `- ${supplier.document}`}
                    </option>
                  ))}
                </select>
                {suppliers.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Nenhum fornecedor cadastrado. <a href="/dashboard/people/new" className="text-blue-600 hover:underline">Cadastre aqui</a>
                  </p>
                )}
              </div>
              
              <Input
                label="Número da Nota Fiscal"
                value={newBill.invoice_number}
                onChange={(e) => setNewBill(prev => ({ ...prev, invoice_number: e.target.value }))}
                placeholder="NF-123456"
              />
              
              <div className="md:col-span-2">
                <Input
                  label="Descrição"
                  value={newBill.description}
                  onChange={(e) => setNewBill(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição da conta"
                  required
                />
              </div>
              
              <Input
                label="Valor"
                type="number"
                step="0.01"
                value={newBill.amount}
                onChange={(e) => setNewBill(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                required
              />
              
              <Input
                label="Data de Vencimento"
                type="date"
                value={newBill.due_date}
                onChange={(e) => setNewBill(prev => ({ ...prev, due_date: e.target.value }))}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={newBill.category}
                  onChange={(e) => setNewBill(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Estoque">Estoque</option>
                  <option value="Utilidades">Utilidades</option>
                  <option value="Limpeza">Limpeza</option>
                  <option value="Equipamentos">Equipamentos</option>
                  <option value="Serviços">Serviços</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  value={newBill.priority}
                  onChange={(e) => setNewBill(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={newBill.notes}
                  onChange={(e) => setNewBill(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowNewBillModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateBill}
                className="flex-1"
              >
                Criar Conta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedBill && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Detalhes da Conta</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Fornecedor</label>
                  <p className="text-lg font-medium text-gray-900">{selectedBill.supplier_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nota Fiscal</label>
                  <p className="text-lg font-medium text-gray-900">{selectedBill.invoice_number}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500">Descrição</label>
                <p className="text-gray-900">{selectedBill.description}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Valor</label>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedBill.amount)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Vencimento</label>
                  <p className="text-gray-900">{formatDate(selectedBill.due_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedBill.status)}`}>
                    {getStatusIcon(selectedBill.status)}
                    <span className="ml-1">{getStatusLabel(selectedBill.status)}</span>
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEditBill(selectedBill);
                }}
                className="flex-1"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              {selectedBill.status === 'pending' && (
                <Button 
                  onClick={() => {
                    handleMarkAsPaid(selectedBill);
                    setShowDetailsModal(false);
                  }}
                  className="flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como Pago
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Bill Modal */}
      {showEditModal && editingBill && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Conta a Pagar</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Fornecedor"
                value={editingBill.supplier_name}
                onChange={(e) => setEditingBill(prev => ({ ...prev, supplier_name: e.target.value }))}
                placeholder="Nome do fornecedor"
                required
              />
              
              <Input
                label="Número da Nota Fiscal"
                value={editingBill.invoice_number}
                onChange={(e) => setEditingBill(prev => ({ ...prev, invoice_number: e.target.value }))}
                placeholder="NF-123456"
              />
              
              <div className="md:col-span-2">
                <Input
                  label="Descrição"
                  value={editingBill.description}
                  onChange={(e) => setEditingBill(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição da conta"
                  required
                />
              </div>
              
              <Input
                label="Valor"
                type="number"
                step="0.01"
                value={editingBill.amount}
                onChange={(e) => setEditingBill(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                required
              />
              
              <Input
                label="Data de Vencimento"
                type="date"
                value={editingBill.due_date}
                onChange={(e) => setEditingBill(prev => ({ ...prev, due_date: e.target.value }))}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={editingBill.category}
                  onChange={(e) => setEditingBill(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Estoque">Estoque</option>
                  <option value="Utilidades">Utilidades</option>
                  <option value="Limpeza">Limpeza</option>
                  <option value="Equipamentos">Equipamentos</option>
                  <option value="Serviços">Serviços</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={editingBill.status}
                  onChange={(e) => setEditingBill(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="overdue">Vencido</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  value={editingBill.priority}
                  onChange={(e) => setEditingBill(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={editingBill.notes}
                  onChange={(e) => setEditingBill(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingBill(null);
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateBill}
                className="flex-1"
              >
                Atualizar Conta
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountsPayablePage;
